import { Badge, Button, Card, Group, Pagination, SegmentedControl, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconBellOff } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { NotificationDto, Paginated } from '@mexp/shared';
import { http } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { notificationKeys } from '@/layout/NotificationBell';
import { formatDateTime, fromNow } from '@/lib/format';

const PAGE_SIZE = 20;

/** The notification list. The client portal passes its own `header`; staff get the standard page header. */
export function NotificationsPage({ header }: { header?: (markAll: ReactNode) => ReactNode } = {}) {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: [...notificationKeys.all, 'page', filter, page],
    queryFn: () => http.get<Paginated<NotificationDto>>('/notifications', { page, limit: PAGE_SIZE, unreadOnly: filter === 'unread' }),
    placeholderData: keepPreviousData,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  const markRead = useMutation({ mutationFn: (id: string) => http.post(`/notifications/${id}/read`), onSuccess: invalidate });
  const markAll = useMutation({ mutationFn: () => http.post('/notifications/read-all'), onSuccess: invalidate });

  const open = (n: NotificationDto) => {
    if (!n.readAt) markRead.mutate(n.id);
    if (n.link) navigate(n.link);
  };
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;

  const markAllButton = (
    <Button variant="light" onClick={() => markAll.mutate()} loading={markAll.isPending}>
      Mark all as read
    </Button>
  );

  return (
    <>
      {header ? (
        header(markAllButton)
      ) : (
        <PageHeader title="Notifications" description="Updates about work assigned to you and requests you follow." actions={markAllButton} />
      )}
      <Card p={0}>
        <Group p="md" justify="space-between">
          <SegmentedControl
            value={filter}
            onChange={(v) => {
              setFilter(v as 'all' | 'unread');
              setPage(1);
            }}
            data={[
              { label: 'All', value: 'all' },
              { label: 'Unread', value: 'unread' },
            ]}
          />
          {list.data && (
            <Text c="dimmed" fz="sm">
              {list.data.total} notification{list.data.total === 1 ? '' : 's'}
            </Text>
          )}
        </Group>
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState icon={IconBellOff} title={filter === 'unread' ? 'No unread notifications' : 'No notifications yet'} />
        ) : (
          <Stack gap={0}>
            {list.data.items.map((n) => (
              <UnstyledButton
                key={n.id}
                onClick={() => open(n)}
                px="md"
                py="sm"
                style={{ borderTop: '1px solid var(--mantine-color-default-border)', background: n.readAt ? undefined : 'var(--mantine-color-brand-light)' }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text fw={n.readAt ? 500 : 650} fz="sm">
                      {n.title}
                    </Text>
                    {n.body && (
                      <Text fz="sm" c="dimmed">
                        {n.body}
                      </Text>
                    )}
                    <Text fz="xs" c="dimmed" title={formatDateTime(n.createdAt)}>
                      {fromNow(n.createdAt)}
                      {n.actorName ? ` · by ${n.actorName}` : ''}
                    </Text>
                  </Stack>
                  {!n.readAt && (
                    <Badge size="sm" variant="filled">
                      New
                    </Badge>
                  )}
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        )}
        {pages > 1 && (
          <Group justify="center" p="md">
            <Pagination total={pages} value={page} onChange={setPage} />
          </Group>
        )}
      </Card>
    </>
  );
}
